import { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Switch, FormControlLabel, TextField,
  CircularProgress, Alert, Collapse, Divider, IconButton, Tooltip, Paper,
} from '@mui/material';
import { CreditCard, Building2, Plus, Trash2, Star } from 'lucide-react';
import { useSubscriptions } from '../hooks/useSubscriptions';
import { ConfirmDialog } from './ConfirmDialog';
import type { CustomPlanOverride } from '../types/institute';

const EMPTY_PLAN: CustomPlanOverride = {
  name: '', description: '', monthlyPrice: 0, annualPrice: 0,
  durationDays: 365, currency: 'INR', isRecommended: false, displayOrder: 0,
  allowMonthly: true, allowAnnual: true,
};

interface Props {
  instituteId: number;
}

export function InstituteSubscriptionPanel({ instituteId }: Props) {
  const { useInstituteSubscriptionSettings, useUpdateInstituteSubscriptionSettings } =
    useSubscriptions();

  const { data: settings, isLoading } = useInstituteSubscriptionSettings(instituteId);
  const updateMutation = useUpdateInstituteSubscriptionSettings(instituteId);

  const [institutePays, setInstitutePays] = useState(false);
  const [useCustomPlans, setUseCustomPlans] = useState(false);
  const [customPlans, setCustomPlans] = useState<CustomPlanOverride[]>([]);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (settings) {
      setInstitutePays(settings.mode === 'InstitutePays');
      setUseCustomPlans(settings.customPlans.length > 0);
      setCustomPlans(settings.customPlans.length > 0 ? settings.customPlans : [{ ...EMPTY_PLAN }]);
    }
  }, [settings]);

  const setField = (index: number, field: keyof CustomPlanOverride, val: unknown) =>
    setCustomPlans((prev) => prev.map((p, i) => i === index ? { ...p, [field]: val } : p));

  const addPlan = () => setCustomPlans((prev) => [...prev, { ...EMPTY_PLAN, displayOrder: prev.length + 1 }]);
  const removePlan = (index: number) => setCustomPlans((prev) => prev.filter((_, i) => i !== index));

  const handleModeToggle = (checked: boolean) => {
    setInstitutePays(checked);
    if (checked) setConfirmOpen(true);
  };

  const handleSave = () => {
    updateMutation.mutate({
      mode: institutePays ? 'InstitutePays' : 'StudentPays',
      customPlans: useCustomPlans ? customPlans.filter((p) => p.name.trim()) : [],
    });
  };

  const handleConfirmInstitutePays = () => {
    setConfirmOpen(false);
    updateMutation.mutate({
      mode: 'InstitutePays',
      customPlans: useCustomPlans ? customPlans.filter((p) => p.name.trim()) : [],
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  return (
    <Box>
      {/* Mode toggle */}
      <Box
        sx={{
          p: 1.5, borderRadius: '8px', mb: 2,
          bgcolor: institutePays ? 'rgba(16,185,129,0.06)' : 'rgba(255,255,255,0.025)',
          border: '1px solid',
          borderColor: institutePays ? 'rgba(16,185,129,0.2)' : 'rgba(255,255,255,0.06)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          {institutePays ? <Building2 size={14} color="#10B981" /> : <CreditCard size={14} color="#8A70D6" />}
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
            {institutePays ? 'Institute Pays' : 'Students Pay'}
          </Typography>
        </Box>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.72rem', display: 'block', mb: 1 }}>
          {institutePays
            ? 'All students are automatically subscribed. No payment required from students.'
            : 'Each student pays for their own subscription via Razorpay.'}
        </Typography>
        <FormControlLabel
          label={<Typography variant="caption" sx={{ fontSize: '0.75rem' }}>Institute pays for all students</Typography>}
          control={
            <Switch
              checked={institutePays}
              onChange={(e) => handleModeToggle(e.target.checked)}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#10B981' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#10B981' },
              }}
            />
          }
          sx={{ mx: 0 }}
        />
      </Box>

      <Divider sx={{ mb: 2 }} />

      {/* Custom plans */}
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>
                Custom plan pricing
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                Override global plans with institute-specific pricing (multiple plans supported)
              </Typography>
            </Box>
          }
          control={
            <Switch
              checked={useCustomPlans}
              onChange={(e) => {
                setUseCustomPlans(e.target.checked);
                if (e.target.checked && customPlans.length === 0)
                  setCustomPlans([{ ...EMPTY_PLAN }]);
              }}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#8A70D6' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#8A70D6' },
              }}
            />
          }
          sx={{ mx: 0, mb: 1, alignItems: 'flex-start' }}
        />

        <Collapse in={useCustomPlans}>
          <Alert severity="info" sx={{ py: 0.5, fontSize: '0.72rem', mb: 1.5 }}>
            These plans are shown to students of this institute instead of global plans.
          </Alert>

          {customPlans.map((plan, index) => (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: 1.5, mb: 1.5, borderRadius: '8px',
                border: '1px solid rgba(255,255,255,0.08)',
                bgcolor: 'rgba(255,255,255,0.02)',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.72rem', color: 'text.secondary' }}>
                  Plan {index + 1}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title={plan.isRecommended ? 'Remove recommended' : 'Mark as recommended'}>
                    <IconButton
                      size="small"
                      onClick={() => setField(index, 'isRecommended', !plan.isRecommended)}
                      sx={{ color: plan.isRecommended ? '#F59E0B' : 'text.disabled' }}
                    >
                      <Star size={13} fill={plan.isRecommended ? '#F59E0B' : 'none'} />
                    </IconButton>
                  </Tooltip>
                  {customPlans.length > 1 && (
                    <Tooltip title="Remove plan">
                      <IconButton size="small" onClick={() => removePlan(index)} sx={{ color: 'error.main' }}>
                        <Trash2 size={13} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <TextField
                  label="Plan Name"
                  value={plan.name}
                  onChange={(e) => setField(index, 'name', e.target.value)}
                  size="small"
                  fullWidth
                  required
                />
                <TextField
                  label="Description"
                  value={plan.description ?? ''}
                  onChange={(e) => setField(index, 'description', e.target.value)}
                  size="small"
                  fullWidth
                />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <FormControlLabel
                    label={<Typography variant="caption" sx={{ fontSize: '0.72rem' }}>Monthly</Typography>}
                    control={
                      <Switch
                        checked={plan.allowMonthly}
                        onChange={(e) => setField(index, 'allowMonthly', e.target.checked)}
                        size="small"
                      />
                    }
                    sx={{ mx: 0 }}
                  />
                  <FormControlLabel
                    label={<Typography variant="caption" sx={{ fontSize: '0.72rem' }}>Annual</Typography>}
                    control={
                      <Switch
                        checked={plan.allowAnnual}
                        onChange={(e) => setField(index, 'allowAnnual', e.target.checked)}
                        size="small"
                      />
                    }
                    sx={{ mx: 0 }}
                  />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <TextField
                    label="Monthly Price (₹)"
                    type="number"
                    value={plan.monthlyPrice}
                    onChange={(e) => setField(index, 'monthlyPrice', parseFloat(e.target.value) || 0)}
                    size="small"
                    fullWidth
                    disabled={!plan.allowMonthly}
                  />
                  <TextField
                    label="Annual Price (₹)"
                    type="number"
                    value={plan.annualPrice}
                    onChange={(e) => setField(index, 'annualPrice', parseFloat(e.target.value) || 0)}
                    size="small"
                    fullWidth
                    disabled={!plan.allowAnnual}
                  />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                  <TextField
                    label="Duration (days)"
                    type="number"
                    value={plan.durationDays}
                    onChange={(e) => setField(index, 'durationDays', parseInt(e.target.value) || 365)}
                    size="small"
                    fullWidth
                    disabled={!plan.allowAnnual}
                    helperText="Annual billing"
                  />
                  <TextField
                    label="Currency"
                    value={plan.currency}
                    onChange={(e) => setField(index, 'currency', e.target.value)}
                    size="small"
                    fullWidth
                  />
                  <TextField
                    label="Display Order"
                    type="number"
                    value={plan.displayOrder}
                    onChange={(e) => setField(index, 'displayOrder', parseInt(e.target.value) || 0)}
                    size="small"
                    fullWidth
                  />
                </Box>
              </Box>
            </Paper>
          ))}

          <Button
            size="small"
            variant="outlined"
            startIcon={<Plus size={13} />}
            onClick={addPlan}
            sx={{ fontSize: '0.75rem', mb: 1 }}
          >
            Add Another Plan
          </Button>
        </Collapse>
      </Box>

      <Button
        variant="contained"
        size="small"
        fullWidth
        onClick={handleSave}
        disabled={
          updateMutation.isPending ||
          (useCustomPlans && customPlans.every((p) => !p.name.trim()))
        }
        startIcon={updateMutation.isPending ? <CircularProgress size={13} /> : undefined}
        sx={{ fontSize: '0.8rem' }}
      >
        Save Subscription Settings
      </Button>

      <ConfirmDialog
        open={confirmOpen}
        title="Enable Institute Pays"
        message="All current students of this institute will be immediately marked as subscribed at no cost to them. Continue?"
        confirmLabel="Yes, Mark All Subscribed"
        severity="warning"
        loading={updateMutation.isPending}
        onConfirm={handleConfirmInstitutePays}
        onCancel={() => { setConfirmOpen(false); setInstitutePays(false); }}
      />
    </Box>
  );
}
